import { authService } from './authService';
import { getServerURL } from './config';
import { WEBSOCKET_EVENTS } from '../constants/orderConstants';

/**
 * WebSocket Service for real-time order updates and notifications
 */
class WebSocketService {
  constructor() {
    this.socket = null;
    this.isConnected = false;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 1000; // Start with 1 second
    this.maxReconnectDelay = 30000; // Max 30 seconds
    this.listeners = new Map();
    this.heartbeatInterval = null;
    this.connectionPromise = null;
  }

  /**
   * Connect to WebSocket server
   * @returns {Promise<boolean>} Connection success status
   */
  async connect() {
    if (this.isConnected || this.connectionPromise) {
      return this.connectionPromise || Promise.resolve(true);
    }

    this.connectionPromise = this._establishConnection();
    return this.connectionPromise;
  }

  /**
   * Establish WebSocket connection
   * @private
   */
  async _establishConnection() {
    try {
      const serverURL = await getServerURL();
      const token = authService.getToken();
      
      if (!token) {
        throw new Error('No authentication token available');
      }

      // Convert HTTP URL to WebSocket URL
      const wsURL = serverURL.replace(/^https?:\/\//, 'wss://').replace(/^http:\/\//, 'ws://');
      const socketURL = `${wsURL}/ws/orders?token=${encodeURIComponent(token)}`;

      this.socket = new WebSocket(socketURL);

      return new Promise((resolve, reject) => {
        const connectionTimeout = setTimeout(() => {
          reject(new Error('WebSocket connection timeout'));
        }, 10000);

        this.socket.onopen = () => {
          clearTimeout(connectionTimeout);
          this.isConnected = true;
          this.reconnectAttempts = 0;
          this.reconnectDelay = 1000;
          this.connectionPromise = null;
          
          // console.log('WebSocket connected to orders service');
          this._startHeartbeat();
          this._emit('connected');
          resolve(true);
        };

        this.socket.onmessage = (event) => {
          this._handleMessage(event);
        };

        this.socket.onclose = (event) => {
          clearTimeout(connectionTimeout);
          this.isConnected = false;
          this.connectionPromise = null;
          this._stopHeartbeat();
          
          // console.log('WebSocket disconnected:', event.code, event.reason);
          this._emit('disconnected', { code: event.code, reason: event.reason });
          
          // Attempt to reconnect unless it was a clean close
          if (event.code !== 1000 && this.reconnectAttempts < this.maxReconnectAttempts) {
            this._scheduleReconnect();
          }
          
          if (this.reconnectAttempts === 0) {
            resolve(false);
          }
        };

        this.socket.onerror = (error) => {
          clearTimeout(connectionTimeout);
          // console.error('WebSocket error:', error);
          this._emit('error', error);
          reject(error);
        };
      });
    } catch (error) {
      this.connectionPromise = null;
      // console.error('Failed to establish WebSocket connection:', error);
      throw error;
    }
  }

  /**
   * Disconnect from WebSocket server
   */
  disconnect() {
    if (this.socket) {
      this.socket.close(1000, 'Client disconnect');
    }
    this._stopHeartbeat();
    this.isConnected = false;
    this.connectionPromise = null;
  }

  /**
   * Subscribe to order updates for specific order
   * @param {string} orderId - Order ID to subscribe to
   */
  subscribeToOrder(orderId) {
    if (!this.isConnected) {
      console.warn('WebSocket not connected. Cannot subscribe to order:', orderId);
      return;
    }

    this._send({
      type: WEBSOCKET_EVENTS.SUBSCRIBE_ORDER,
      data: { order_id: orderId }
    });
  }

  /**
   * Unsubscribe from order updates
   * @param {string} orderId - Order ID to unsubscribe from
   */
  unsubscribeFromOrder(orderId) {
    if (!this.isConnected) {
      return;
    }

    this._send({
      type: WEBSOCKET_EVENTS.UNSUBSCRIBE_ORDER,
      data: { order_id: orderId }
    });
  }

  /**
   * Subscribe to all order updates
   */
  subscribeToAllOrders() {
    if (!this.isConnected) {
      console.warn('WebSocket not connected. Cannot subscribe to all orders');
      return;
    }

    this._send({
      type: WEBSOCKET_EVENTS.SUBSCRIBE_ALL_ORDERS
    });
  }

  /**
   * Subscribe to reviewer workload updates
   */
  subscribeToReviewerUpdates() {
    if (!this.isConnected) {
      console.warn('WebSocket not connected. Cannot subscribe to reviewer updates');
      return;
    }

    this._send({
      type: WEBSOCKET_EVENTS.SUBSCRIBE_REVIEWER_UPDATES
    });
  }

  /**
   * Add event listener
   * @param {string} event - Event name
   * @param {Function} callback - Event callback
   */
  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event).add(callback);
  }

  /**
   * Remove event listener
   * @param {string} event - Event name
   * @param {Function} callback - Event callback
   */
  off(event, callback) {
    if (this.listeners.has(event)) {
      this.listeners.get(event).delete(callback);
    }
  }

  /**
   * Handle incoming WebSocket message
   * @private
   */
  _handleMessage(event) {
    try {
      const message = JSON.parse(event.data);
      
      switch (message.type) {
        case WEBSOCKET_EVENTS.ORDER_UPDATED:
          this._emit('orderUpdated', message.data);
          break;
          
        case WEBSOCKET_EVENTS.ORDER_STATUS_CHANGED:
          this._emit('orderStatusChanged', message.data);
          break;
          
        case WEBSOCKET_EVENTS.ORDER_ASSIGNED:
          this._emit('orderAssigned', message.data);
          break;
          
        case WEBSOCKET_EVENTS.ORDER_COMMENT_ADDED:
          this._emit('orderCommentAdded', message.data);
          break;
          
        case WEBSOCKET_EVENTS.ORDER_FLAG_ADDED:
          this._emit('orderFlagAdded', message.data);
          break;
          
        case WEBSOCKET_EVENTS.REVIEWER_WORKLOAD_UPDATED:
          this._emit('reviewerWorkloadUpdated', message.data);
          break;
          
        case WEBSOCKET_EVENTS.NOTIFICATION:
          this._emit('notification', message.data);
          break;
          
        case 'pong':
          // Heartbeat response
          break;
          
        default:
          console.warn('Unknown WebSocket message type:', message.type);
      }
    } catch (error) {
      console.error('Error parsing WebSocket message:', error);
    }
  }

  /**
   * Send message to WebSocket server
   * @private
   */
  _send(message) {
    if (this.socket && this.isConnected) {
      this.socket.send(JSON.stringify(message));
    }
  }

  /**
   * Emit event to listeners
   * @private
   */
  _emit(event, data) {
    if (this.listeners.has(event)) {
      this.listeners.get(event).forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error('Error in WebSocket event listener:', error);
        }
      });
    }
  }

  /**
   * Schedule reconnection attempt
   * @private
   */
  _scheduleReconnect() {
    this.reconnectAttempts++;
    
    setTimeout(() => {
      // console.log(`Attempting to reconnect (${this.reconnectAttempts}/${this.maxReconnectAttempts})...`);
      this.connect().catch(error => {
        // console.error('Reconnection failed:', error);
      });
    }, this.reconnectDelay);

    // Exponential backoff with jitter
    this.reconnectDelay = Math.min(
      this.reconnectDelay * 2 + Math.random() * 1000,
      this.maxReconnectDelay
    );
  }

  /**
   * Start heartbeat to keep connection alive
   * @private
   */
  _startHeartbeat() {
    this.heartbeatInterval = setInterval(() => {
      if (this.isConnected) {
        this._send({ type: 'ping' });
      }
    }, 30000); // Send ping every 30 seconds
  }

  /**
   * Stop heartbeat
   * @private
   */
  _stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  /**
   * Get connection status
   * @returns {boolean} Connection status
   */
  getConnectionStatus() {
    return this.isConnected;
  }

  /**
   * Get connection statistics
   * @returns {Object} Connection statistics
   */
  getConnectionStats() {
    return {
      isConnected: this.isConnected,
      reconnectAttempts: this.reconnectAttempts,
      listenerCount: Array.from(this.listeners.values()).reduce(
        (total, listeners) => total + listeners.size, 0
      )
    };
  }
}

// Create singleton instance
const websocketService = new WebSocketService();

export { websocketService };
export default websocketService;